import React from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { styled } from "@mui/system";
import { Doughnut } from "react-chartjs-2";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useTranslation } from "react-i18next";

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

export default function ProbaExport({ probaData, threshold = 0.3 }) {
  const { t } = useTranslation();
  const interpolateColor = (vuln) => {
    const vulnPercentage = vuln * 100;
    if (vulnPercentage >= 20) {
      return "#FF0000";
    }
    const r = Math.round(0 + 255 * (vulnPercentage / 20));
    const g = Math.round(255 - 255 * (vulnPercentage / 20));
    const b = 0;
    return `rgb(${r}, ${g}, ${b})`;
  };

  const StageBox = styled(Box)(({ theme, shadowSize = 1 }) => ({
    backgroundColor: "white",
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.grey[300]}`,
    textAlign: "center",
    width: { sm: "95%", xs: "90%" },
    boxShadow: theme.shadows[shadowSize],
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  }));

  // Extract level data and threat data
  const levelData = probaData.level;
  const threatData = probaData.data;

  // Create stages for level data
  const levelStages = [
    {
      name: "Initial Access",
      probability: levelData.p1 * 100,
      tactics: {
        reconnaissance: levelData["reconnaissance"],
        resourceDevelopment: levelData["resource-development"],
        initialAccess: levelData["initial-access"],
      },
    },
    {
      name: "Execution",
      probability: levelData.p2 * 100,
      tactics: {
        execution: levelData["execution"],
        persistence: levelData["persistence"],
        privilegeEscalation: levelData["privilege-escalation"],
      },
    },
    {
      name: "Persistence",
      probability: levelData.p3 * 100,
      tactics: {
        defenseEvasion: levelData["defense-evasion"],
        credentialAccess: levelData["credential-access"],
        discovery: levelData["discovery"],
      },
    },
    {
      name: "Privilege Escalation",
      probability: levelData.p4 * 100,
      tactics: {
        lateralMovement: levelData["lateral-movement"],
        collection: levelData["collection"],
        commandAndControl: levelData["command-and-control"],
      },
    },
    {
      name: "Impact",
      probability: levelData.p5 * 100,
      tactics: {
        exfiltration: levelData["exfiltration"],
        impact: levelData["impact"],
      },
    },
  ];

  return (
    <Box sx={{ bgcolor: "#f9f9f9" }}>
      {/* Level Data Section - shown only once */}
      <Box
        id={`tlm-threat-1`}
        sx={{
          width: "210mm",
          margin: "auto",
          marginBottom: "20px",
          boxShadow: 3,
          borderRadius: 2,
          bgcolor: "#f9f9f9",
          padding: "20px",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <Typography
          sx={{
            textAlign: "start",
            marginBottom: "10px",
            fontWeight: "bold",
          }}
          variant="h5"
        >
          Section 3 : Vulnerability Assessment
        </Typography>
        <Typography
          sx={{
            textAlign: "start",
            fontSize: "0.875rem",
            marginBottom: "10px",
          }}
        >
          {`This section presents a comprehensive vulnerability assessment based on two key elements: a theoretical evaluation of successful attack probabilities using the MITRE ATT&CK framework and the Cyber Kill Chain model, and a realistic scenario-based probability assessment.
 
The graph illustrates the number of vulnerabilities discovered through external attack surface scans, mapped to different phases of the Cyber Kill Chain. Each phase is associated with specific MITRE ATT&CK techniques, which are categorized tactics used by threat actors during cyber attacks. The percentages represent the estimated probability of an attacker successfully progressing through each phase of the Kill Chain, based on the identified vulnerabilities.
 
The accompanying table provides a more nuanced view of attack probabilities for different threat scenarios. It shows a range of minimum and maximum probabilities for successful attacks, taking into account the exploitation of discovered vulnerabilities. This realistic assessment considers various factors that could influence the likelihood of a successful attack, providing a practical perspective on the organization's vulnerability landscape.
 
Together, these elements offer a holistic view of the organization's security posture, combining theoretical attack models with real-world probability estimates to guide risk management and mitigation strategies.`.replace(
            /\. /g,
            ".\n\n"
          )}
        </Typography>
        <Typography
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            marginBottom: "10px",
          }}
          variant="h6"
        >
          Attack Success Probability Across Kill Chain Phases
        </Typography>

        <Stack direction={"column"} spacing={2} sx={{ alignItems: "center" }}>
          <Box sx={{ flexGrow: 1, width: "100%" }}>
            {levelStages.map((stage, stageIndex) => (
              <React.Fragment key={stage.name}>
                <StageBox>
                  <Typography
                    variant="h6"
                    sx={{ fontSize: "1rem" }} // Reduced from 1.25rem
                  >
                    {stage.name} : {stage.probability.toFixed(0)}%
                  </Typography>
                  <Stack
                    direction={"row"}
                    spacing={1}
                    justifyContent="center"
                    mt={1}
                  >
                    {Object.entries(stage.tactics).map(([tactic, count]) => (
                      <Typography
                        key={tactic}
                        variant="body2"
                        sx={{ fontSize: "0.75rem" }} // Reduced from 0.875rem
                      >
                        {tactic.replace(/([A-Z])/g, " $1").trim()} : {count}
                      </Typography>
                    ))}
                  </Stack>
                </StageBox>
                {stageIndex < levelStages.length - 1 && (
                  <Box sx={{ textAlign: "center", my: 1 }}>
                    <ArrowDownwardIcon
                      sx={{
                        fontSize: "24px", // Reduced from 30px
                        color: "grey.600",
                      }}
                    />
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
        </Stack>
      </Box>

      {/* Threat Data Table */}
      <Box
        id={`tlm-threat-1`}
        sx={{
          width: "210mm",
          margin: "auto",
          boxShadow: 3,
          borderRadius: 2,
          bgcolor: "#f9f9f9",
          padding: "20px",
          boxSizing: "border-box",
        }}
      >
        <Typography
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            marginBottom: "10px",
          }}
          variant="h6"
        >
          Probability Range of Successful Arracks by Threat Type
        </Typography>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: "bold",
                    width: "20%",
                    fontSize: "0.75rem",
                  }}
                >
                  Threat Type
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: "bold",
                    width: "30%",
                    fontSize: "0.75rem",
                  }}
                >
                  Probability
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: "bold",
                    width: "50%",
                    fontSize: "0.75rem",
                  }}
                >
                  Description
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {threatData.map((threatItem, index) => {
                const gaugeData = {
                  datasets: [
                    {
                      data: [
                        threatItem?.max * 100,
                        100 - threatItem?.max * 100,
                      ],
                      backgroundColor: [
                        interpolateColor(threatItem?.max),
                        "grey",
                      ],
                      borderWidth: 0,
                      circumference: 180,
                      rotation: 270,
                    },
                    {
                      data: [
                        threatItem?.max * 100,
                        2,
                        100 - threatItem?.max * 100 - 2,
                      ],
                      backgroundColor: [
                        "transparent",
                        "#000000",
                        "transparent",
                      ],
                      borderWidth: 0,
                      circumference: 180,
                      rotation: 270,
                    },
                  ],
                };

                const gaugeOptions = {
                  plugins: {
                    datalabels: {
                      display: false,
                    },
                    legend: { display: false },
                    tooltip: { enabled: false },
                  },
                  cutout: "80%",
                  rotation: -90,
                  circumference: 180,
                  maintainAspectRatio: false,
                };

                return (
                  <TableRow key={index}>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>
                      {t(threatItem?.threat)}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{ width: 120, height: 60, position: "relative" }}
                        >
                          <Doughnut data={gaugeData} options={gaugeOptions} />
                          <Typography
                            variant="body1"
                            sx={{
                              position: "absolute",
                              top: "80%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",

                              fontWeight: "bold",
                              fontSize: "0.75rem", // Reduced from 18px
                              whiteSpace: "nowrap",
                            }}
                          >
                            [{(threatItem?.min * 100).toFixed(0)}% —{" "}
                            {(threatItem?.max * 100).toFixed(0)}%]
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" sx={{ fontSize: "0.75rem" }}>
                        {threatItem?.justification}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
